import { PrimaryButton } from "components/Button/style";
import { MaisFiltros } from "components/ButtonModal";
import { CustomInput } from "components/CustomInput";
import { CustomInputModal } from "components/CustomInputModal";
import { CustomDatePicker } from "components/DatePicker";
import { DropdownSelect } from "components/DropdownSelect";
import { TooltipBootstrap } from "components/TooltipBootstrap";
import { useDivisoes } from "hooks/useDivisoes";
import { useEmpresas } from "hooks/useEmpresas";
import { useCallback } from "react";
import { Col, Form, Row } from "react-bootstrap";
import { Controller, useForm, useWatch } from "react-hook-form";
import { FILTROS_SOLICITACAO_VAZIOS } from "../../constants/filtrosSolicitacao";
import { IFiltrosSolicitacao } from "../../interfaces/IFiltrosSolicitacao";
import {
  BotoesContainer,
  CampoMaisFiltros,
  Container,
  IconeInfo,
  MaisFiltrosContainer,
  MaisFiltrosDropdownWrapper,
  Title,
} from "./style";

interface IProps {
  onBuscar: (filtros: IFiltrosSolicitacao) => void;
}

export function Filtros({ onBuscar }: IProps) {
  const { control, handleSubmit, reset, setValue } =
    useForm<IFiltrosSolicitacao>({ defaultValues: FILTROS_SOLICITACAO_VAZIOS });
  const idEmpresa = useWatch({ control, name: "idEmpresa" });
  const idDivisao = useWatch({ control, name: "idDivisao" });
  const periodo = useWatch({ control, name: "periodoSolicitacao" });
  const [dataInicio, dataFim] = periodo ?? [];
  const { empresasOptions } = useEmpresas();
  const { divisoesOptions } = useDivisoes(idEmpresa);

  // DropdownSelect chama onSelect(null) num efeito que depende dele: onSelect precisa ser estável.
  const selecionarEmpresa = useCallback(
    (valor: string | number | null) => {
      setValue("idEmpresa", valor ? Number(valor) : null);
      setValue("idDivisao", null);
    },
    [setValue]
  );

  const selecionarDivisao = useCallback(
    (valor: string | number | null) => {
      setValue("idDivisao", valor ? Number(valor) : null);
    },
    [setValue]
  );

  function handleLimparMaisFiltros() {
    setValue("idProcesso", []);
    setValue("periodoSolicitacao", null);
  }

  function handleLimpar() {
    reset(FILTROS_SOLICITACAO_VAZIOS);
    onBuscar(FILTROS_SOLICITACAO_VAZIOS);
  }

  return (
    <Container>
      <Title>Filtros</Title>
      <hr />
      <Form noValidate onSubmit={handleSubmit((filtros) => onBuscar(filtros))}>
        <Row>
          <Col md={2}>
            <Form.Group>
              <Form.Label>
                PDI{" "}
                <TooltipBootstrap
                  childrenToolTip={
                    <span>
                      Serão considerados todos os PDIs que contenham a palavra
                      informada
                    </span>
                  }
                >
                  <span>
                    <IconeInfo />
                  </span>
                </TooltipBootstrap>
              </Form.Label>
              <Controller
                control={control}
                name="pdi"
                render={({ field }) => (
                  <CustomInput
                    type="text"
                    placeholder="Busque por PDI"
                    style={{ height: "2.2rem" }}
                    {...field}
                  />
                )}
              />
            </Form.Group>
          </Col>
          <Col md={2}>
            <Form.Group>
              <Form.Label>Empresa</Form.Label>
              <DropdownSelect
                options={empresasOptions}
                selectedValue={idEmpresa}
                onSelect={selecionarEmpresa}
                searchable
              />
            </Form.Group>
          </Col>
          <Col md={2}>
            <Form.Group>
              <Form.Label>Divisão</Form.Label>
              <DropdownSelect
                options={divisoesOptions}
                selectedValue={idDivisao}
                onSelect={selecionarDivisao}
                searchable
                disabled={!idEmpresa}
              />
            </Form.Group>
          </Col>
          <Col md={2}>
            <Form.Group>
              <Form.Label>Placa</Form.Label>
              <Controller
                control={control}
                name="placa"
                render={({ field: { value, onChange } }) => (
                  <CustomInputModal
                    label="Filtrar em massa"
                    value={value}
                    placeholder="Busque por placa"
                    onConfirm={onChange}
                    validar={false}
                    style={{ width: "100%" }}
                    placeholderModal="Digite ou cole as placas e tecle ENTER"
                    labelItem="Placa"
                  />
                )}
              />
            </Form.Group>
          </Col>
          <Col md={2}>
            <BotoesContainer>
              <PrimaryButton
                type="button"
                outline
                variante="primary"
                onClick={handleLimpar}
              >
                Limpar
              </PrimaryButton>
              <PrimaryButton type="submit">Buscar</PrimaryButton>
            </BotoesContainer>
          </Col>
          <Col md={2} style={{ paddingTop: "5px" }}>
            <MaisFiltrosContainer>
              <Form.Group>
                <MaisFiltrosDropdownWrapper>
                  <MaisFiltros
                    limparTudoOnClick={handleLimparMaisFiltros}
                    cancelarOnClick={handleLimparMaisFiltros}
                    handleClickAplicarFiltros={() => {}}
                    widthModal="lg"
                    submit
                  >
                    <CampoMaisFiltros>
                      <Form.Group>
                        <Form.Label>ID Processo</Form.Label>
                        <Controller
                          control={control}
                          name="idProcesso"
                          render={({ field: { value, onChange } }) => (
                            <CustomInputModal
                              label="Filtrar em massa"
                              value={value}
                              placeholder="Busque por ID"
                              onConfirm={onChange}
                              validar={false}
                              style={{ width: "100%" }}
                              placeholderModal="Digite ou cole os IDs dos processos e tecle ENTER"
                              labelItem="ID Processo"
                            />
                          )}
                        />
                      </Form.Group>
                    </CampoMaisFiltros>

                    <CampoMaisFiltros>
                      <Form.Group>
                        <Form.Label>Período de solicitação</Form.Label>
                        <Controller
                          control={control}
                          name="periodoSolicitacao"
                          render={({ field: { onChange } }) => (
                            <CustomDatePicker
                              placeholder="Selecione o período"
                              rangeSelect
                              startDate={dataInicio ?? undefined}
                              endDate={dataFim ?? undefined}
                              onChange={(datas) => onChange(datas)}
                            />
                          )}
                        />
                      </Form.Group>
                    </CampoMaisFiltros>
                  </MaisFiltros>
                </MaisFiltrosDropdownWrapper>
              </Form.Group>
            </MaisFiltrosContainer>
          </Col>
        </Row>
      </Form>
    </Container>
  );
}
